import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-bissa-blue hover:text-bissa-gold transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
          <h1 className="text-4xl font-bold text-bissa-blue mb-2">Privacy Policy</h1>
          <p className="text-gray-600">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-8 prose prose-lg max-w-none">
          <h2>1. Information We Collect</h2>
          
          <h3>Personal Information</h3>
          <p>We collect information you provide directly to us, such as:</p>
          <ul>
            <li>Account information (name, email address, password)</li>
            <li>Profile information and preferences</li>
            <li>Contact data you upload to the CRM system</li>
            <li>Communication records and support requests</li>
            <li>Billing and payment information (processed securely through Stripe)</li>
          </ul>

          <h3>Usage Information</h3>
          <p>We automatically collect certain information about your use of the Service:</p>
          <ul>
            <li>Log data (IP address, browser type, operating system)</li>
            <li>Usage patterns and feature interaction data</li>
            <li>Performance and error information</li>
            <li>Device and location information</li>
          </ul>

          <h3>Cookies and Similar Technologies</h3>
          <p>
            We use cookies, web beacons, and similar technologies to enhance your experience, 
            analyze usage, and provide personalized content. See our Cookie Policy for details.
          </p>

          <h2>2. How We Use Your Information</h2>
          <p>We use the collected information to:</p>
          <ul>
            <li>Provide, maintain, and improve the Bissa.ai Service</li>
            <li>Process transactions and send billing notifications</li>
            <li>Respond to your comments, questions, and customer service requests</li>
            <li>Send technical notices, updates, security alerts, and administrative messages</li>
            <li>Monitor and analyze trends, usage, and activities</li>
            <li>Detect, investigate, and prevent fraudulent transactions and abuse</li>
            <li>Personalize and improve your experience</li>
          </ul>

          <h2>3. Information Sharing and Disclosure</h2>
          <p>We do not sell, trade, or rent your personal information to third parties. We may share information in limited circumstances:</p>
          
          <h3>Service Providers</h3>
          <p>
            We work with third-party service providers who perform services on our behalf, such as:
          </p>
          <ul>
            <li>Payment processing (Stripe)</li>
            <li>Cloud hosting and infrastructure</li>
            <li>Analytics and performance monitoring</li>
            <li>Customer support tools</li>
          </ul>

          <h3>Legal Requirements</h3>
          <p>We may disclose information if required by law or in response to:</p>
          <ul>
            <li>Valid legal process (subpoenas, court orders)</li>
            <li>Government requests or investigations</li>
            <li>Protection of our rights, property, or safety</li>
            <li>Emergency situations involving personal safety</li>
          </ul>

          <h3>Business Transfers</h3>
          <p>
            In connection with any merger, sale, or transfer of assets, user information may be 
            transferred as part of the transaction.
          </p>

          <h2>4. Data Security</h2>
          <p>We implement appropriate technical and organizational measures to protect your information:</p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Secure authentication and access controls</li>
            <li>Regular security audits and monitoring</li>
            <li>Employee training on data protection</li>
            <li>Compliance with industry security standards</li>
          </ul>

          <h2>5. Data Retention</h2>
          <p>
            We retain your information for as long as your account is active or as needed to provide services. 
            When you delete your account, we will delete your personal information within 30 days, except where 
            we need to retain it for legitimate business purposes or legal requirements.
          </p>

          <h2>6. Your Rights and Choices</h2>
          
          <h3>Access and Control</h3>
          <p>You have the right to:</p>
          <ul>
            <li>Access and review your personal information</li>
            <li>Update or correct inaccurate information</li>
            <li>Delete your account and associated data</li>
            <li>Export your data in a portable format</li>
            <li>Object to or restrict certain processing activities</li>
          </ul>

          <h3>Communication Preferences</h3>
          <p>
            You can opt out of promotional communications by following unsubscribe instructions 
            or updating your account preferences. Service-related communications cannot be opted out of.
          </p>

          <h2>7. International Data Transfers</h2>
          <p>
            Your information may be transferred to and processed in countries other than your own. 
            We ensure appropriate safeguards are in place to protect your information in accordance 
            with this Privacy Policy.
          </p>

          <h2>8. Children's Privacy</h2>
          <p>
            Bissa.ai is not intended for children under 13 years of age. We do not knowingly collect 
            personal information from children under 13. If we become aware of such collection, 
            we will delete the information immediately.
          </p>

          <h2>9. GDPR Compliance (EU Users)</h2>
          <p>If you are located in the European Union, you have additional rights under GDPR:</p>
          <ul>
            <li>Right to be informed about data processing</li>
            <li>Right of access to your personal data</li>
            <li>Right to rectification of inaccurate data</li>
            <li>Right to erasure ("right to be forgotten")</li>
            <li>Right to restrict processing</li>
            <li>Right to data portability</li>
            <li>Right to object to processing</li>
            <li>Rights related to automated decision making</li>
          </ul>

          <h2>10. CCPA Compliance (California Users)</h2>
          <p>California residents have specific rights under the California Consumer Privacy Act:</p>
          <ul>
            <li>Right to know what personal information is collected</li>
            <li>Right to delete personal information</li>
            <li>Right to opt-out of the sale of personal information</li>
            <li>Right to non-discrimination for exercising CCPA rights</li>
          </ul>

          <h2>11. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy periodically. We will notify you of any material changes 
            by posting the new Privacy Policy on this page and updating the "Last updated" date. 
            We encourage you to review this Privacy Policy periodically.
          </p>

          <h2>12. Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <ul>
            <li>Through the support system in your Bissa.ai dashboard</li>
            <li>By email at <a href="mailto:leslie@iabadvisors.com" className="text-bissa-blue hover:underline">leslie@iabadvisors.com</a></li>
            <li>Through our contact form on the website</li>
          </ul>
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="font-semibold mb-2">Bissa.ai</p>
            <p>
              Email: <a href="mailto:leslie@iabadvisors.com" className="text-bissa-blue hover:underline">leslie@iabadvisors.com</a><br />
              Website: <a href="https://bissa.ai" className="text-bissa-blue hover:underline">https://bissa.ai</a>
            </p>
          </div>

          <div className="mt-12 p-6 bg-green-50 rounded-lg border border-green-200">
            <h3 className="text-green-800 font-semibold mb-2">Your Privacy Matters</h3>
            <p className="text-green-700">
              At Bissa.ai, we're committed to protecting your privacy and handling your data responsibly. 
              If you have any concerns or questions about how we handle your information, don't hesitate 
              to reach out to our privacy team.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;